/**
 * Backend-Driven Live Mode Hook
 * PRD-45: Production Run Contract Implementation
 *
 * This hook replaces the mock/live.ts simulation with real backend-driven flow.
 * It uses WebSocket events from the backend to update the flow state.
 */

import type { HITLDecision, HITLRequest } from "@/types/agents";
import { apiClient } from "@/services/api-client";
import {
  normalizePendingHITLRequest,
  type NormalizedPendingHITLRequest,
} from "@/services/agents/hitl-request-normalizer";
import { shouldUseMockData } from "@/lib/mock-data/utils";
import { getSession } from "next-auth/react";
import { useActiveAccountStore } from "@/features/accounts/store/use-active-account-store";
import type { FlowNodeData, MtfTimeframeData } from "../types/flow.types";
import type { AutomationLevel } from "@/features/trading-preferences/trading-preferences.service";
import {
  AGENTS_CONFIG,
  normalizeAgentKey,
} from "@/features/office/config/agents.config";
import {
  type NodeStatus,
  type RunStatus,
  type RunSnapshot,
  getRunSnapshot,
  listFlowRuns,
  startFlowRun,
} from "../services/run-contract.service";
import { canonicalTranscriptPayloadToMessage } from "../utils/agent-stream";

const LOG_PREFIX = "[BackendLive]";

/** Convert backend NodeStatus to frontend FlowNodeStatus */
function mapNodeStatus(status: NodeStatus): FlowNodeData["status"] {
  const mapping: Record<NodeStatus, FlowNodeData["status"]> = {
    idle: "idle",
    running: "running",
    success: "success",
    error: "error",
    waiting_hitl: "waiting_hitl",
    skipped: "skipped",
    filtered: "filtered",
  };
  return mapping[status] || "idle";
}

/** Convert RunSnapshot to FlowNodeData array (SIG-712: AGENTS_CONFIG is SSOT). */
export function snapshotToNodes(snapshot: RunSnapshot): FlowNodeData[] {
  return snapshot.nodes.map((node) => {
    const agentKey = normalizeAgentKey(node.agent_key, node.node_id);
    const agent = agentKey ? AGENTS_CONFIG[agentKey] : undefined;
    const isCurrent = node.node_id === snapshot.current_node_id;

    // Build data array from node data
    const data: { label: string; value: string }[] = node.data || [];

    // For Ben Kim node, add MTF specific data
    if (node.node_id === "signal_filter" && node.overall_score !== undefined) {
      data.push({
        label: "Confidence",
        value: `${Math.round(node.overall_score * 100)}%`,
      });
      if (
        node.conflicting_timeframes &&
        node.conflicting_timeframes.length > 0
      ) {
        data.push({
          label: "Conflicts",
          value: node.conflicting_timeframes.join(", "),
        });
      }
    }

    // Map backend MTF timeframes to frontend MtfTimeframeData
    const mtfTimeframes: MtfTimeframeData[] | undefined = node.mtf_timeframes
      ? node.mtf_timeframes.map((tf) => ({
          timeframe: tf.timeframe as MtfTimeframeData["timeframe"],
          screenshot_url: tf.screenshot_url || undefined,
          indicators: tf.indicators as MtfTimeframeData["indicators"],
          price_vs_vwap: tf.price_vs_vwap || "at",
          trend_direction: tf.trend_direction,
          visual_score: tf.visual_score,
        }))
      : undefined;

    const primaryTf = node.mtf_timeframes?.[0];

    return {
      id: node.node_id,
      name: agent?.role || node.name,
      status: mapNodeStatus(node.status),
      duration: node.duration_ms || 0,
      current: isCurrent,
      agent: agent
        ? { name: agent.name, avatarUrl: agent.avatarUrl }
        : undefined,
      agentKey: agentKey || undefined,
      data: data.length > 0 ? data : undefined,
      message: node.message || undefined,
      // Ben Kim specific fields
      chartCaptured: node.mtf_timeframes
        ? node.mtf_timeframes.length > 0
        : undefined,
      visionAnalyzed: node.overall_score !== undefined,
      mtfTimeframes,
      overallScore: node.overall_score,
      overallDirection: node.overall_direction,
      conflictingTimeframes: node.conflicting_timeframes,
      screenshotUrl: primaryTf?.screenshot_url || undefined,
      indicatorsExtracted:
        primaryTf?.indicators as FlowNodeData["indicatorsExtracted"],
      priceVsVwap: primaryTf?.price_vs_vwap,
      priceVsSr: primaryTf?.price_vs_sr,
    } as FlowNodeData;
  });
}

// =============================================================================
// Live Control Interface
// =============================================================================

export type HITLRefreshResult =
  "pending" | "cleared" | "replaced" | "failed" | "stale";

export interface BackendLiveControl {
  /** Start the live connection when using the hook form */
  start?: () => Promise<void>;
  /** Stop the live connection */
  stop: () => void;
  resumeFromHITL: (
    approved: boolean,
    options?: {
      expectedRequestId?: string;
      decision?: HITLDecision;
    },
  ) => boolean | void | Promise<boolean | void>;
  /** Check if currently paused at HITL */
  isPausedAtHITL: () => boolean;
  refreshHITLState?: (expectedRequestId: string) => Promise<HITLRefreshResult>;
  /** Current run ID */
  runId: string | null;
}

export interface BackendLiveCallbacks {
  onRunStart?: (info: {
    id: string;
    timestamp: string;
    duration: number;
  }) => void;
  onUpdate: (nodes: FlowNodeData[]) => void;
  onRunEnd?: (status?: RunStatus) => void;
  onHITLRequired?: (request: HITLRequest) => void;
  onHITLCleared?: (requestId: string) => void;
  onHITLSubmissionError?: (requestId: string, error: Error) => void;
  onError?: (error: Error) => void;
  onConnectionStatusChange?: (
    status:
      | "starting"
      | "connecting"
      | "connected"
      | "reconnecting"
      | "disconnected"
      | "error",
    error?: string | null,
  ) => void;
  onTranscriptEvent?: (message: {
    id: string;
    agent: string;
    agentAvatar: string;
    content: string;
    timestamp: Date;
    type: "message" | "handoff" | "decision" | "chart";
    metadata?: {
      toAgent?: string;
      confidence?: number;
      score?: number;
    };
  }) => void;
}

export interface BackendLiveOptions {
  /** Attach to an already-running flow (spectate) instead of starting a new one. */
  attachRunId?: string;
  automationMode?: AutomationLevel;
  /** If true, use the mock simulation as fallback when backend is unavailable */
  fallbackToMock?: boolean;
  /** Trading symbol for a new run (Wave 2 — no longer hardcoded ES-only). */
  symbol?: string;
  /** Optional strategy registry id for a new run. */
  strategyId?: number | null;
  /** Trading group that owns the run and downstream copy fanout. */
  tradingGroupId?: number | null;
}

interface FlowSocketMessage {
  type: string;
  channel?: string;
  payload?: Record<string, unknown>;
  timestamp?: string;
}

interface PendingHITLItem extends NormalizedPendingHITLRequest {
  run_id: string;
}

function getSelectedRunAccount(): {
  brokerAccountId: number | null;
  environment: "demo" | "live" | undefined;
} {
  const state = useActiveAccountStore.getState();
  const account = state.activeAccount ?? state.primaryAccount ?? null;
  const brokerAccountId =
    state.activeAccountId ?? state.primaryAccountId ?? account?.id ?? null;
  const environment =
    account?.environment === "demo" || account?.environment === "live"
      ? account.environment
      : undefined;

  return { brokerAccountId, environment };
}

type TradingGroupLookup = {
  id: number;
  active: boolean;
  leader_account: number | { id: number };
};

async function resolveTradingGroupIdForLeader(
  brokerAccountId: number,
): Promise<number> {
  const response = await apiClient.get<
    TradingGroupLookup[] | { items: TradingGroupLookup[] }
  >("/trading-groups");
  const rows = Array.isArray(response.data)
    ? response.data
    : response.data.items;
  const matches = rows.filter((group) => {
    const leaderId =
      typeof group.leader_account === "number"
        ? group.leader_account
        : group.leader_account.id;
    return group.active && leaderId === brokerAccountId;
  });
  if (matches.length !== 1) {
    throw new Error(
      matches.length === 0
        ? "The selected account is not the leader of an active trading group."
        : "The selected account maps to multiple active trading groups; execution is blocked.",
    );
  }
  return matches[0].id;
}

function resolveFlowWebSocketUrl(websocketUrl: string): string {
  if (/^wss?:\/\//i.test(websocketUrl)) {
    return websocketUrl;
  }

  const configuredBase =
    process.env.NEXT_PUBLIC_WEBSOCKET_URL ||
    process.env.NEXT_PUBLIC_BACKEND_API_URL?.replace(/^http/i, "ws") ||
    "ws://localhost:8000";
  const normalizedBase = configuredBase
    .replace(/\/+$/, "")
    .replace(/\/api\/?$/, "")
    .replace(/\/ws\/?$/, "");
  const normalizedPath = websocketUrl.startsWith("/")
    ? websocketUrl
    : `/${websocketUrl}`;

  return `${normalizedBase}${normalizedPath}`;
}

async function fetchPendingHitlForRun(
  runId: string,
  expectedRequestId?: string | null,
): Promise<{ ambiguous: boolean; match: PendingHITLItem | null }> {
  const response = await apiClient.get<unknown>("/hitl/pending");
  const responseDetail =
    response.data &&
    typeof response.data === "object" &&
    !Array.isArray(response.data)
      ? ["detail", "message", "error"]
          .map((key) => (response.data as Record<string, unknown>)[key])
          .find((value): value is string =>
            Boolean(typeof value === "string" && value.trim()),
          )
      : undefined;
  if (response.status < 200 || response.status >= 300) {
    throw new Error(
      `Pending HITL discovery for run ${runId} failed: HTTP ${response.status}${
        responseDetail ? ` — ${responseDetail.trim()}` : ""
      }`,
    );
  }
  if (!Array.isArray(response.data)) {
    throw new Error(
      `Pending HITL discovery for run ${runId} expected an array response (HTTP ${response.status})`,
    );
  }
  const requests = response.data
    .map(normalizePendingHITLRequest)
    .filter(
      (request): request is PendingHITLItem =>
        request !== null && typeof request.run_id === "string",
    );
  const matchingRun = requests.filter((request) => request.run_id === runId);
  if (expectedRequestId) {
    return {
      ambiguous: false,
      match:
        matchingRun.find((request) => request.id === expectedRequestId) ?? null,
    };
  }
  return {
    ambiguous: matchingRun.length > 1,
    match: matchingRun.length === 1 ? matchingRun[0] : null,
  };
}

async function resolvePendingHitlForRun(
  runId: string,
  expectedRequestId?: string | null,
  attempts = 5,
  delayMs = 250,
): Promise<PendingHITLItem | null> {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const { ambiguous, match } = await fetchPendingHitlForRun(
      runId,
      expectedRequestId,
    );
    if (match) {
      return match;
    }
    if (ambiguous) {
      return null;
    }
    if (attempt < attempts - 1) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }

  return null;
}

function isTerminalRunStatus(status: RunStatus | "error"): boolean {
  // "error" is the backend's raw terminal spelling; adapted snapshots map it
  // to "failed", but a raw status leaking through must still read terminal.
  return (
    status === "completed" ||
    status === "rejected" ||
    status === "failed" ||
    status === "cancelled" ||
    status === "error"
  );
}

function parseTerminalRunStatus(value: unknown): RunStatus | null {
  switch (String(value || "").toLowerCase()) {
    case "completed":
      return "completed";
    case "cancelled":
      return "cancelled";
    case "rejected":
      return "rejected";
    case "error":
    case "failed":
      return "failed";
    default:
      return null;
  }
}

// =============================================================================
// Hybrid Live Function (Backend + Mock Fallback)
// =============================================================================

/**
 * Start live mode with backend-first strategy and mock fallback
 */
export async function startBackendLive(
  callbacks: BackendLiveCallbacks,
  options: BackendLiveOptions = {},
): Promise<BackendLiveControl> {
  const {
    automationMode = "semi_automated",
    // Wave 0: never mask backend failures with demo choreography unless the
    // caller explicitly opts in AND shouldUseMockData() is true.
    fallbackToMock = false,
    attachRunId,
    symbol = "ES",
    strategyId = null,
    tradingGroupId = null,
  } = options;

  console.log(
    `${LOG_PREFIX} ${attachRunId ? "Attaching to run" : "Starting backend live mode"}...`,
  );

  callbacks.onConnectionStatusChange?.("starting");

  try {
    let response: {
      success: boolean;
      run_id: string;
      websocket_url?: string;
      initial_snapshot?: RunSnapshot;
      error?: string;
    };
    if (attachRunId) {
      // Spectate: the run already exists (autonomous pipeline) — attach only,
      // never create. Everything downstream keys off response.run_id.
      response = { success: true, run_id: attachRunId };
    } else {
      const selectedAccount = getSelectedRunAccount();
      if (selectedAccount.brokerAccountId == null) {
        throw new Error(
          "Select a broker account before starting a Live Office run (J1).",
        );
      }
      const resolvedTradingGroupId =
        tradingGroupId ??
        (await resolveTradingGroupIdForLeader(selectedAccount.brokerAccountId));
      const resolvedSymbol = (symbol || "ES").trim().toUpperCase() || "ES";
      response = await startFlowRun({
        automation_level_override: automationMode,
        broker_account_id: selectedAccount.brokerAccountId,
        trading_group_id: resolvedTradingGroupId,
        environment_override: selectedAccount.environment,
        symbol: resolvedSymbol,
        strategy_id: strategyId ?? undefined,
      });
    }

    if (!response.success) {
      throw new Error(response.error || "Failed to start run");
    }

    console.log(
      `${LOG_PREFIX} Backend run ${attachRunId ? "attached" : "started"}:`,
      response.run_id,
    );

    callbacks.onRunStart?.({
      id: response.run_id,
      timestamp: new Date().toISOString(),
      duration: 0,
    });

    let isActive = true;
    let isPausedAtHITL = false;
    let ws: WebSocket | null = null;
    let socketConnected = false;
    let reconnectAttempts = 0;
    let reconnectTimeout: NodeJS.Timeout | null = null;
    let pollInterval: NodeJS.Timeout | null = null;
    let stopPollingAfterSnapshotRecovery = false;
    let pendingTerminalSnapshotHint: RunStatus | "unknown" | null = null;
    let terminalFinalized = false;
    let snapshotRequestSequence = 0;
    let latestNotifiedHitlRequestId: string | null = null;
    const seenTranscriptEventIds = new Set<string>();

    const forwardTranscriptEvent = (payload: Record<string, unknown>) => {
      const transcriptMessage = canonicalTranscriptPayloadToMessage(
        payload,
        response.run_id,
      );
      if (!transcriptMessage) return;
      const eventKey = `${response.run_id}:${transcriptMessage.id}`;
      if (seenTranscriptEventIds.has(eventKey)) return;
      seenTranscriptEventIds.add(eventKey);
      callbacks.onTranscriptEvent?.(transcriptMessage);
    };
    let hitlNotificationGeneration = 0;
    const inFlightHitlRequestIds = new Set<string>();
    const submittedHitlRequestIds = new Set<string>();
    const notifiedHitlRequestIds = new Set<string>();

    const stopPolling = () => {
      if (pollInterval) {
        clearInterval(pollInterval);
        pollInterval = null;
      }
    };

    const stopReconnect = () => {
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
        reconnectTimeout = null;
      }
    };

    const stopSocket = () => {
      socketConnected = false;
      if (ws) {
        ws.onopen = null;
        ws.onmessage = null;
        ws.onclose = null;
        ws.onerror = null;
        try {
          ws.close(1000, "cleanup");
        } catch {
          // no-op
        }
        ws = null;
      }
    };

    const finalizeTerminal = (status: RunStatus, error?: Error) => {
      if (terminalFinalized) return;
      terminalFinalized = true;
      isActive = false;
      isPausedAtHITL = false;
      pendingTerminalSnapshotHint = null;
      stopPollingAfterSnapshotRecovery = false;
      stopReconnect();
      stopPolling();
      stopSocket();
      callbacks.onConnectionStatusChange?.("disconnected");
      if (error) callbacks.onError?.(error);
      callbacks.onRunEnd?.(status);
    };

    const canApplySnapshot = (requestSequence: number) =>
      isActive && requestSequence === snapshotRequestSequence;

    const invalidateCurrentHitl = (requestId = latestNotifiedHitlRequestId) => {
      if (requestId === null) return;
      if (requestId === latestNotifiedHitlRequestId) {
        isPausedAtHITL = false;
        latestNotifiedHitlRequestId = null;
        hitlNotificationGeneration += 1;
      }
      callbacks.onHITLCleared?.(requestId);
    };

    const notifyPendingHitl = (hitlRequest: PendingHITLItem) => {
      if (
        inFlightHitlRequestIds.has(hitlRequest.id) ||
        submittedHitlRequestIds.has(hitlRequest.id)
      ) {
        return;
      }
      isPausedAtHITL = true;
      if (hitlRequest.id === latestNotifiedHitlRequestId) {
        return;
      }
      latestNotifiedHitlRequestId = hitlRequest.id;
      notifiedHitlRequestIds.add(hitlRequest.id);
      hitlNotificationGeneration += 1;
      callbacks.onHITLRequired?.(hitlRequest);
    };

    const applySnapshot = async (
      runId: string,
      snapshot: RunSnapshot,
      requestSequence: number,
    ) => {
      if (!canApplySnapshot(requestSequence)) {
        return;
      }
      callbacks.onUpdate(snapshotToNodes(snapshot));

      if (snapshot.hitl_pending) {
        const requestAlreadyHandled =
          snapshot.hitl_request_id !== null &&
          (inFlightHitlRequestIds.has(snapshot.hitl_request_id) ||
            submittedHitlRequestIds.has(snapshot.hitl_request_id));
        if (!requestAlreadyHandled) {
          const hitlRequest = await resolvePendingHitlForRun(
            runId,
            snapshot.hitl_request_id,
          );
          if (!canApplySnapshot(requestSequence)) {
            return;
          }
          if (hitlRequest) {
            notifyPendingHitl(hitlRequest);
          }
        }
      } else {
        invalidateCurrentHitl();
      }

      if (!canApplySnapshot(requestSequence)) {
        return;
      }
      if (isTerminalRunStatus(snapshot.status)) {
        finalizeTerminal(
          snapshot.status,
          snapshot.status === "failed"
            ? new Error(snapshot.error_message || "Office flow failed")
            : undefined,
        );
      }
    };

    const syncSnapshot = async (
      runId: string,
      hitlFence?: { generation: number; requestId: string },
    ) => {
      const requestSequence = ++snapshotRequestSequence;
      const snapshot = await getRunSnapshot(runId);
      if (
        hitlFence &&
        (hitlNotificationGeneration !== hitlFence.generation ||
          latestNotifiedHitlRequestId !== hitlFence.requestId)
      ) {
        const preservesCurrentHitl =
          snapshot.hitl_pending &&
          latestNotifiedHitlRequestId !== null &&
          snapshot.hitl_request_id === latestNotifiedHitlRequestId;
        if (!preservesCurrentHitl) {
          return snapshot;
        }
      }
      await applySnapshot(runId, snapshot, requestSequence);
      return snapshot;
    };

    const settleSnapshotRecovery = (snapshot: RunSnapshot) => {
      if (!isActive || !stopPollingAfterSnapshotRecovery || !socketConnected) {
        return;
      }
      if (isTerminalRunStatus(snapshot.status)) {
        pendingTerminalSnapshotHint = null;
        return;
      }
      if (pendingTerminalSnapshotHint !== null) {
        return;
      }
      stopPollingAfterSnapshotRecovery = false;
      stopPolling();
      callbacks.onConnectionStatusChange?.("connected");
    };

    const startPolling = () => {
      if (pollInterval || !isActive) {
        return;
      }

      pollInterval = setInterval(async () => {
        if (!isActive) return;
        try {
          const snapshot = await syncSnapshot(response.run_id);
          settleSnapshotRecovery(snapshot);
        } catch (err) {
          console.error(`${LOG_PREFIX} Polling error:`, err);
        }
      }, 2000);
    };

    const markSnapshotRefreshDegraded = (context: string, error: unknown) => {
      console.error(`${LOG_PREFIX} ${context}:`, error);
      if (!isActive) return;
      const detail = error instanceof Error ? error.message : String(error);
      stopPollingAfterSnapshotRecovery = true;
      callbacks.onConnectionStatusChange?.(
        "reconnecting",
        `${context}: ${detail}; snapshot retry scheduled`,
      );
      startPolling();
    };

    const accessToken = (await getSession())?.accessToken;
    if (!accessToken) {
      throw new Error("No valid session token available for Office live mode");
    }

    const connectRunSocket = () => {
      if (!isActive) return;
      socketConnected = false;

      callbacks.onConnectionStatusChange?.(
        reconnectAttempts > 0 ? "reconnecting" : "connecting",
      );

      const socketUrl = resolveFlowWebSocketUrl(
        response.websocket_url || `/ws/v2/flow/${response.run_id}`,
      );
      ws = new WebSocket(socketUrl, ["bearer", accessToken]);

      ws.onopen = () => {
        socketConnected = true;
        reconnectAttempts = 0;
        callbacks.onConnectionStatusChange?.(
          stopPollingAfterSnapshotRecovery ? "reconnecting" : "connected",
        );
      };

      ws.onmessage = async (event) => {
        try {
          const message = JSON.parse(event.data) as FlowSocketMessage;
          const payload = (message.payload || {}) as Record<string, unknown>;

          switch (message.type) {
            case "connection_established": {
              try {
                const snapshot = await syncSnapshot(response.run_id);
                settleSnapshotRecovery(snapshot);
              } catch (snapshotError) {
                markSnapshotRefreshDegraded(
                  "Connection catch-up snapshot refresh failed",
                  snapshotError,
                );
              }
              return;
            }
            case "transcript_history": {
              const historyEvents = Array.isArray(payload.events)
                ? payload.events
                : [];
              for (const transcriptEvent of historyEvents) {
                if (transcriptEvent && typeof transcriptEvent === "object") {
                  forwardTranscriptEvent(
                    transcriptEvent as Record<string, unknown>,
                  );
                }
              }
              return;
            }
            case "transcript_event":
              forwardTranscriptEvent(payload);
              return;
            case "flow_started":
            case "node_started":
            case "node_completed": {
              // SIG-872: the refetch is the seam, deliberately — not the WS
              // payload. `node_completed` carries only a clipped preview of the
              // node's display rows (see node_output_projection.preview_node_output);
              // the REST snapshot is the single ordered authority and carries the
              // full-length values the drawers render. Merging the preview into
              // cache instead would race the refetch, invent per-node ordering the
              // socket does not guarantee, and pin drawers to truncated values.
              // The payload is a nudge; the snapshot is the truth. This was dead
              // only because the snapshot returned `output: {}` for every node.
              const snapshot = await syncSnapshot(response.run_id);
              settleSnapshotRecovery(snapshot);
              return;
            }
            case "hitl_decided": {
              const decidedRequestId =
                typeof payload.request_id === "string" && payload.request_id
                  ? payload.request_id
                  : notifiedHitlRequestIds.size === 1
                    ? latestNotifiedHitlRequestId
                    : null;
              if (decidedRequestId !== null) {
                submittedHitlRequestIds.add(decidedRequestId);
                invalidateCurrentHitl(decidedRequestId);
              }
              try {
                const snapshot = await syncSnapshot(response.run_id);
                settleSnapshotRecovery(snapshot);
              } catch (snapshotError) {
                markSnapshotRefreshDegraded(
                  "HITL decision snapshot refresh failed",
                  snapshotError,
                );
              }
              return;
            }
            case "flow_completed": {
              const terminalHint =
                parseTerminalRunStatus(payload.final_status) ??
                parseTerminalRunStatus(payload.status);
              try {
                const snapshot = await syncSnapshot(response.run_id);
                if (!isActive) {
                  return;
                }
                if (isTerminalRunStatus(snapshot.status)) {
                  return;
                }
                pendingTerminalSnapshotHint = terminalHint ?? "unknown";
                markSnapshotRefreshDegraded(
                  terminalHint
                    ? `Flow completion awaiting authoritative ${terminalHint} snapshot`
                    : "Flow completion awaiting authoritative terminal snapshot",
                  new Error(
                    `Authoritative snapshot is still ${snapshot.status}`,
                  ),
                );
              } catch (snapshotError) {
                pendingTerminalSnapshotHint = terminalHint ?? "unknown";
                markSnapshotRefreshDegraded(
                  terminalHint
                    ? `Flow completion ${terminalHint} snapshot refresh failed`
                    : "Flow completion snapshot refresh failed",
                  snapshotError,
                );
              }
              return;
            }
            case "flow_error": {
              let terminalError = new Error(
                String(payload.error || "Office flow failed"),
              );
              try {
                await syncSnapshot(response.run_id);
              } catch (snapshotError) {
                if (!payload.error && snapshotError instanceof Error) {
                  terminalError = snapshotError;
                }
              } finally {
                finalizeTerminal("failed", terminalError);
              }
              return;
            }
            case "hitl_requested": {
              try {
                const snapshot = await syncSnapshot(response.run_id);
                settleSnapshotRecovery(snapshot);
              } catch (snapshotError) {
                markSnapshotRefreshDegraded(
                  "HITL request snapshot refresh failed",
                  snapshotError,
                );
              }
              return;
            }
            default:
              return;
          }
        } catch (err) {
          console.error(`${LOG_PREFIX} Failed to parse WS message:`, err);
        }
      };

      ws.onclose = (event) => {
        socketConnected = false;
        callbacks.onConnectionStatusChange?.(
          event.code === 1000 ? "disconnected" : "reconnecting",
          event.reason || null,
        );
        if (!isActive) {
          return;
        }
        stopPollingAfterSnapshotRecovery = true;
        startPolling();
        if (
          event.code === 1000 ||
          event.code === 4001 ||
          event.code === 4003 ||
          event.code === 4401 ||
          event.code === 4403
        ) {
          return;
        }
        if (reconnectAttempts >= 5) {
          callbacks.onConnectionStatusChange?.(
            "error",
            "Flow websocket reconnect failed",
          );
          return;
        }
        const delay = Math.min(1000 * 2 ** reconnectAttempts, 10000);
        reconnectTimeout = setTimeout(() => {
          reconnectAttempts += 1;
          connectRunSocket();
        }, delay);
      };

      ws.onerror = () => {
        socketConnected = false;
        stopPollingAfterSnapshotRecovery = true;
        callbacks.onConnectionStatusChange?.(
          "reconnecting",
          "Flow websocket error",
        );
        startPolling();
      };
    };

    const initialSnapshot =
      response.initial_snapshot || (await getRunSnapshot(response.run_id));
    try {
      await applySnapshot(
        response.run_id,
        initialSnapshot,
        ++snapshotRequestSequence,
      );
    } catch (snapshotError) {
      markSnapshotRefreshDegraded(
        "Initial snapshot HITL discovery failed",
        snapshotError,
      );
    }
    if (isActive) {
      callbacks.onConnectionStatusChange?.("connecting");
      connectRunSocket();
    }

    return {
      stop: () => {
        isActive = false;
        stopReconnect();
        stopPolling();
        stopSocket();
        callbacks.onConnectionStatusChange?.("disconnected");
      },
      resumeFromHITL: async (
        approved: boolean,
        options?: {
          expectedRequestId?: string;
          decision?: HITLDecision;
        },
      ) => {
        if (
          !isActive ||
          !isPausedAtHITL ||
          latestNotifiedHitlRequestId === null ||
          (options?.expectedRequestId !== undefined &&
            options.expectedRequestId !== latestNotifiedHitlRequestId) ||
          inFlightHitlRequestIds.has(latestNotifiedHitlRequestId) ||
          submittedHitlRequestIds.has(latestNotifiedHitlRequestId)
        ) {
          return false;
        }
        const decision = options?.decision ?? {
          action: approved ? "approve" : "reject",
        };
        if ((decision.action !== "reject") !== approved) {
          return false;
        }
        const boundRequestId = latestNotifiedHitlRequestId;
        const boundGeneration = hitlNotificationGeneration;
        const previousPauseState = isPausedAtHITL;
        inFlightHitlRequestIds.add(boundRequestId);
        isPausedAtHITL = false;
        console.log(`${LOG_PREFIX} HITL decision:`, approved);
        try {
          try {
            const decisionResponse = await apiClient.post<unknown>(
              `/hitl/${encodeURIComponent(boundRequestId)}/decide`,
              decision,
            );
            if (decisionResponse.status >= 400) {
              const detail =
                decisionResponse.data &&
                typeof decisionResponse.data === "object" &&
                "detail" in decisionResponse.data &&
                typeof decisionResponse.data.detail === "string"
                  ? decisionResponse.data.detail.trim()
                  : "";
              throw new Error(
                detail ||
                  decisionResponse.error?.message ||
                  `Failed to submit HITL decision: ${decisionResponse.status}`,
              );
            }
            submittedHitlRequestIds.add(boundRequestId);
          } catch (err) {
            console.error(`${LOG_PREFIX} Failed to submit HITL decision:`, err);
            const submissionError =
              err instanceof Error
                ? err
                : new Error("Failed to submit HITL decision");
            if (
              isActive &&
              hitlNotificationGeneration === boundGeneration &&
              latestNotifiedHitlRequestId === boundRequestId
            ) {
              isPausedAtHITL = previousPauseState;
            }
            if (isActive) {
              callbacks.onHITLSubmissionError?.(
                boundRequestId,
                submissionError,
              );
            }
            return false;
          }
          if (!isActive) {
            return true;
          }
          try {
            await syncSnapshot(response.run_id, {
              generation: boundGeneration,
              requestId: boundRequestId,
            });
          } catch (err) {
            markSnapshotRefreshDegraded(
              "HITL decision snapshot refresh failed",
              err,
            );
          }
          console.log(`${LOG_PREFIX} HITL decision submitted`);
          return true;
        } finally {
          inFlightHitlRequestIds.delete(boundRequestId);
        }
      },
      isPausedAtHITL: () => isPausedAtHITL,
      refreshHITLState: async (expectedRequestId: string) => {
        if (
          !isActive ||
          !isPausedAtHITL ||
          latestNotifiedHitlRequestId !== expectedRequestId
        ) {
          return "stale";
        }
        try {
          const snapshot = await syncSnapshot(response.run_id);
          settleSnapshotRecovery(snapshot);
          if (
            isPausedAtHITL &&
            latestNotifiedHitlRequestId === expectedRequestId
          ) {
            return "pending";
          }
          return latestNotifiedHitlRequestId === null ? "cleared" : "replaced";
        } catch (snapshotError) {
          markSnapshotRefreshDegraded(
            "Expired HITL request snapshot refresh failed",
            snapshotError,
          );
          return "failed";
        }
      },
      runId: response.run_id,
    };
  } catch (err) {
    console.error(`${LOG_PREFIX} Backend live failed:`, err);

    // Fall back to mock simulation only if mock data is enabled
    if (fallbackToMock && shouldUseMockData()) {
      console.log(`${LOG_PREFIX} Falling back to mock simulation`);
      // Import dynamically to avoid circular dependencies
      const { startLive } = await import("../mock/live");
      const mockControl = startLive(callbacks, { automationMode });

      return {
        stop: mockControl.stop,
        resumeFromHITL: mockControl.resumeFromHITL,
        isPausedAtHITL: mockControl.isPausedAtHITL,
        runId: null,
      };
    }

    throw err;
  }
}

// =============================================================================
// Spectate (attach to a run the autonomous pipeline already started)
// =============================================================================

export function pickActiveRun<T extends { run_id: string; status: string }>(
  runs: T[],
): T | null {
  // SIG-702: within a status tier, prefer Office Live over StrategyRunner
  // spectate runs (run_id prefix `runner-`) so dual-active residue never
  // silently attaches the wrong graph.
  const preferLive = (candidates: T[]): T | null => {
    if (!candidates.length) return null;
    return (
      candidates.find((r) => !r.run_id.startsWith("runner-")) ??
      candidates[0] ??
      null
    );
  };
  return (
    preferLive(runs.filter((r) => r.status === "running")) ??
    preferLive(runs.filter((r) => r.status === "paused")) ??
    preferLive(runs.filter((r) => r.status === "pending"))
  );
}

/**
 * Attach to an already-active flow run, if one exists. Resolves null when the
 * desk is genuinely idle (honest idle — never fakes a run).
 */
export async function spectateBackendLive(
  callbacks: BackendLiveCallbacks,
): Promise<BackendLiveControl | null> {
  const runs = await listFlowRuns(10);
  const active = pickActiveRun(runs);
  if (!active) return null;
  return startBackendLive(callbacks, {
    attachRunId: active.run_id,
    fallbackToMock: false,
  });
}
